import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Shield, 
  Scale, 
  Lock, 
  Percent, 
  Users, 
  HeartHandshake,
  Clock
} from 'lucide-react';

const WhyChooseUsSection = () => {
  return (
    <section id="why-choose-us" className="py-20 lg:py-28 bg-muted/30">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
            The Blue Lion Difference
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Why Choose Us?
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We are not just another claims company. We are a regulated law firm 
            with the expertise and authority to fight for your rights.
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {/* Feature 1 - Highlighted */}
          <Card className="elevated-card border-border/60 overflow-hidden">
            <CardContent className="p-8 relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold to-gold-light" />
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-gold to-gold-light shadow-gold">
                  <Scale className="w-6 h-6 text-gold-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                    SRA Regulated Law Firm
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Unlike claims management companies, we are a law firm. This means high standards, better protection, direct access to qualified solicitors and more options to pursue your claim to get the best outcome.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feature 2 */}
          <Card className="elevated-card border-border/60 overflow-hidden">
            <CardContent className="p-8 relative">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-accent/10">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                    Direct Access to Solicitors
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Speak directly with qualified legal professionals who understand the intricacies of car finance claims. No middlemen, no delays.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feature 3 */}
          <Card className="elevated-card border-border/60 overflow-hidden">
            <CardContent className="p-8 relative">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-accent/10">
                  <Percent className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                    Transparent Fees
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We operate on a No Win, No Fee basis with clear, upfront pricing. No hidden charges, no surprises - just honest legal representation.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feature 4 */}
          <Card className="elevated-card border-border/60 overflow-hidden">
            <CardContent className="p-8 relative">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-accent/10">
                  <Lock className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                    Data Security
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Your personal information is protected by strict legal confidentiality requirements and state-of-the-art data security measures.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trust Indicators Strip */}
        <div className="bg-primary rounded-2xl p-8 lg:p-10">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-gold" />
              </div>
              <span className="text-sm font-medium text-primary-foreground/90">
                Client-First Approach
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-gold" />
              </div>
              <span className="text-sm font-medium text-primary-foreground/90">
                Fast Response Times
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <span className="text-sm font-medium text-primary-foreground/90">
                SRA Regulated
              </span>
            </div>
          </div>
        </div>

        {/* Law Firm vs CMC Comparison */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4 p-6 rounded-2xl bg-card border border-border shadow-md">
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-primary" />
              <span className="text-sm font-medium text-foreground">
                Regulated Law Firm
              </span>
            </div>
            <div className="hidden sm:block w-px bg-border" />
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              <span className="text-sm font-medium text-foreground">
                SRA Number: 815166
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { WhyChooseUsSection };
